import { DialogProps } from '@reach/dialog';
import { useMutation } from '@tanstack/react-query';
import {
  Card,
  Dialog,
  ErrorMessage,
  Grid,
  Icon,
  Loader,
  Stack,
  Text,
} from 'components/ui';
import { format } from 'date-fns';
import { useEnquiry } from 'hooks/use-enquiry';
import { ApiClient } from 'lib/api/client';
import { CALLBACK_REQUEST, OFFER_RECEIVED } from 'lib/helpers';
import { useStore } from 'lib/store';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { DATE_FORMAT } from 'settings/config';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';

interface Props extends DialogProps {
  onDismiss?: () => void;
  enquiryId?: string;
  listingId?: string;
}

interface Offer {
  [key: string]: string;
}

const fields = {
  price: 'Price',
  price_type: 'Price Type',
  offer_validity: 'Offer Validity',
  comments: 'Comments',
  preferred_delivery_date: 'Preferred Delivery Date',
};

export const formatStringToKeyValue = (offer: Offer): React.ReactNode => {
  return Object.entries(offer).map(([key, value], index) => (
    <div key={index}>
      {key !== 'preferred_delivery_date' && (
        <>
          <strong>{fields[key as keyof typeof fields]}:</strong>{' '}
          {key === 'price' ? '$' : ''}
          {value}
        </>
      )}
      {key === 'preferred_delivery_date' && (
        <>
          <strong>{fields[key as keyof typeof fields]}:</strong>{' '}
          {format(new Date(value), DATE_FORMAT.listing)}
        </>
      )}
      <br />
    </div>
  ));
};

const List = styled(Card)`
  display: grid;
  grid-template-columns: 1fr max-content;
  padding: ${globalTheme.space[2]};
  .delete-enquiry {
    transition: 0.1s ease-in-out all;
    cursor: pointer;
    height: max-content;
    padding: ${globalTheme.space[0]};
    &:hover {
      scale: 1.2;
    }
  }
`;

export const InterestedSellerDialog = ({
  isOpen,
  onDismiss,
  enquiryId,
  listingId,
}: Props) => {
  const activeBuyerOrgId = useStore((store) => store.activeBuyerOrgId);
  const { data, isLoading, isError, refetch } = useEnquiry({
    listingId: listingId as string,
    enquiryId: enquiryId as string,
  });

  const { mutate } = useMutation(() =>
    new ApiClient().EnquiryApi.orgMarketplaceListingEnquiryReadCreate(
      {
        listingId: listingId || '',
        buyerOrgId: activeBuyerOrgId as string,
        listingEnquiryId: enquiryId as string,
      },

      {
        onSuccess: () => refetch(),
      }
    )
  );

  const type: string = `${
    data?.message.includes(CALLBACK_REQUEST)
      ? 'Callback'
      : data?.message.includes(OFFER_RECEIVED)
      ? 'Offer'
      : 'Enquiry'
  }`;
  const { mutate: deleteEnquiry } = useMutation(
    () =>
      new ApiClient().EnquiryApi.orgMarketplaceListingEnquiryDestroy({
        listingId: listingId || '',
        buyerOrgId: activeBuyerOrgId as string,
        listingEnquiryId: enquiryId as string,
      }),
    {
      onSuccess: () => {
        toast.success(`${type} deleted sucessfully`);
        onDismiss?.();
      },
      onError: () => {
        toast.error(`Error occured while deleting ${type}`);
      },
    }
  );

  useEffect(() => {
    if (data && !data.read_timestamp) {
      mutate();
    }
  }, [data, mutate]);

  function parseAndRenderOfferString(inputString: string): React.ReactNode {
    const jsonString = inputString.replace('OFFER RECEIVED: ', '');
    try {
      const offerObject: Record<string, any> = JSON.parse(jsonString);
      return formatStringToKeyValue(offerObject);
    } catch (e) {
      const lines = jsonString.split('\n').filter((line) => line.trim() !== '');
      const offerObject: Record<string, string> = {};

      let currentKey = '';
      const knownKeys = [
        'price',
        'offer_validity',
        'preferred_delivery_date',
        'comments',
      ];

      lines.forEach((line) => {
        const [key, ...valueParts] = line.split(':');

        let isConfiguredKey = false;
        let internalKey = '';

        if (key && valueParts.length > 0) {
          const cleanKey = key.trim().toLowerCase().replace(/ /g, '_');
          internalKey = cleanKey;
          if (cleanKey === 'price') internalKey = 'price';
          if (cleanKey === 'valid_until') internalKey = 'offer_validity';

          if (knownKeys.includes(internalKey)) {
            isConfiguredKey = true;
          }
        }

        if (isConfiguredKey) {
          currentKey = internalKey;
          offerObject[internalKey] = valueParts.join(':').trim();
        } else if (currentKey) {
          offerObject[currentKey] += '\n' + line.trim();
        }
      });

      if (Object.keys(offerObject).length === 0) {
        return <Text>{jsonString}</Text>;
      }

      return formatStringToKeyValue(offerObject);
    }
  }

  return (
    <Dialog title={type} isOpen={isOpen} onDismiss={onDismiss}>
      {isLoading && (
        <>
          <Grid alignItems='center' justifyContent='center'>
            <Loader />
          </Grid>
          <Text textAlign='center'> Loading...</Text>
        </>
      )}
      {isError && <ErrorMessage>Error fetching EOIs details.</ErrorMessage>}
      {!isLoading && data && (
        <List>
          <Stack space={0}>
            <Text fontSize={1} fontWeight='semiBold'>
              {data.name}
            </Text>
            <Text fontSize={1} fontWeight='semiBold'>
              {data.mobile}
            </Text>
            {!data?.message.includes(OFFER_RECEIVED) &&
              !data?.message.includes(CALLBACK_REQUEST) && (
                <Text fontSize={1}>{data.message}</Text>
              )}
            {data?.message.includes(OFFER_RECEIVED) && (
              <Text fontSize={1}>
                {parseAndRenderOfferString(data.message)}
              </Text>
            )}
            {data?.message.includes(CALLBACK_REQUEST) && (
              <Text fontSize={1}>
                {data.message
                  .replace('CALLBACK REQUEST: ', '')
                  .replace(/"/g, '')}
              </Text>
            )}
          </Stack>
          <div onClick={() => deleteEnquiry()} className='delete-enquiry'>
            <Icon component='CancelCircleIcon' size={2} />
          </div>
        </List>
      )}
    </Dialog>
  );
};
